import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';

/**
 * Progreso (Fase 6, docs/DATA_MODEL.md §10, docs/PRODUCT_SPEC.md §10).
 *
 * El peso diario **si** esta aqui desde el 21/09/2026. Vivia en `nutrition.ts`
 * porque sin peso no arrancaba el motor de energia y tuvo que existir en la
 * Fase 4; esa razon desaparecio con la retirada de la nutricion, y el motivo
 * para moverlo es mas fuerte que la simetria: `daily_weights` es una senal
 * CORPORAL del atleta —§5 y §10 del encargo— y dejarla en el fichero que se
 * retira la habria arrastrado consigo.
 *
 * **No hay fotografias corporales** y no las habra: es una decision expresa
 * del usuario. El comparador de fechas trabaja solo con numeros.
 */

// --------------------------------------------------------------------------
// Perimetros
// --------------------------------------------------------------------------

export const measurementSideSchema = z.enum(['right', 'left']);
export type MeasurementSide = z.infer<typeof measurementSideSchema>;

/**
 * Una medicion de cinta metrica, semanal.
 *
 * Cada perimetro es opcional **por separado**. Obligar a rellenar los cinco
 * para poder guardar uno termina en que no se guarda ninguno, y la cintura
 * sola ya es util: es la que cruza el arbol de decision del §9 de
 * `NUTRITION_ENGINE.md`.
 */
export const bodyMeasurementSchema = syncColumnsSchema.extend({
  measuredOn: localDateSchema,
  waistCm: z.number().min(30).max(250).nullable(),
  chestCm: z.number().min(30).max(250).nullable(),
  armCm: z.number().min(10).max(100).nullable(),
  thighCm: z.number().min(20).max(150).nullable(),
  calfCm: z.number().min(10).max(100).nullable(),
  /** Lado medido cuando se diferencia. Nulo = no se distingue. */
  side: measurementSideSchema.nullable(),
  note: z.string().max(200).nullable(),
});

export type BodyMeasurement = z.infer<typeof bodyMeasurementSchema>;

// --------------------------------------------------------------------------
// Pasos diarios
// --------------------------------------------------------------------------

/**
 * De donde salen los pasos. Solo `manual`: cualquier otro valor seria dar por
 * hecha una integracion sin verificar (§11 de ARCHITECTURE.md).
 */
export const activitySourceSchema = z.enum(['manual']);
export type ActivitySourceId = z.infer<typeof activitySourceSchema>;

/**
 * Los pasos de un dia, uno por dia (migracion 0028).
 *
 * Existen para una sola cosa: que el NEAT del §2.2 de NUTRITION_ENGINE.md deje
 * de suponerse. Cero es un dato valido —un dia en cama—, y por eso un dia sin
 * fila NO es un dia con cero pasos: es un dia que no se anoto.
 */
export const dailyActivitySchema = syncColumnsSchema.extend({
  activityDate: localDateSchema,
  steps: z.number().int().min(0).max(100000),
  source: activitySourceSchema,
  activeMinutes: z.number().int().min(0).max(1440).nullable(),
});

export type DailyActivity = z.infer<typeof dailyActivitySchema>;

/** Los perimetros que se registran, en el orden en el que se piden. */
// --------------------------------------------------------------------------
// Peso corporal
// --------------------------------------------------------------------------

/**
 * Una pesada. Opcional: el §5 del encargo prohibe expresamente convertir el
 * peso en requisito para acceder al entrenamiento basico.
 *
 * `conditionsStandard` distingue una pesada comparable —en ayunas, misma
 * bascula, misma hora— de una que no lo es, que es lo que permite al §10
 * reducir la comparabilidad en vez de fingir que todas las medidas valen igual.
 */
export const dailyWeightSchema = syncColumnsSchema.extend({
  measuredOn: localDateSchema,
  weightKg: z.number().min(30).max(300),
  /**
   * Bioimpedancia, en tanto por CIENTO: 18 es 18 %, no 0,18.
   *
   * La unidad se declara aqui porque el mismo nombre viajo en dos unidades
   * distintas por el proyecto hasta el 02/09/2026 y no dio error de tipos: los
   * dos son `number`. Senal secundaria, y no se usa sola para decidir nada.
   */
  bodyFatPct: z.number().min(1).max(70).nullable(),
  conditionsStandard: z.boolean(),
  note: z.string().max(200).nullable(),
});

export type DailyWeight = z.infer<typeof dailyWeightSchema>;

export const MEASUREMENT_FIELDS = ['waistCm', 'chestCm', 'armCm', 'thighCm', 'calfCm'] as const;
export type MeasurementField = (typeof MEASUREMENT_FIELDS)[number];

// --------------------------------------------------------------------------
// Check-in semanal
// --------------------------------------------------------------------------

export const checkinDecisionSchema = z.enum(['maintain', 'adjust']);
export type CheckinDecision = z.infer<typeof checkinDecisionSchema>;

export const checkinSourceSchema = z.enum(['algorithm', 'claude', 'user']);
export type CheckinSource = z.infer<typeof checkinSourceSchema>;

/**
 * El check-in semanal, congelado.
 *
 * `metrics` y `changes` se guardan tal y como se calcularon, no se recalculan
 * al leer: la interpretacion de la semana 12 tiene que seguir siendo legible
 * cuando los datos de origen ya se hayan corregido. Es la misma razon por la
 * que `shopping_lists` guarda su `pantry_snapshot`.
 */
export const weeklyCheckinSchema = syncColumnsSchema.extend({
  weekStart: localDateSchema,
  metrics: z.record(z.string(), z.unknown()).default({}),
  summary: z.string().default(''),
  interpretation: z.string().default(''),
  decision: checkinDecisionSchema.default('maintain'),
  changes: z.array(z.record(z.string(), z.unknown())).default([]),
  source: checkinSourceSchema.default('algorithm'),
});

export type WeeklyCheckin = z.infer<typeof weeklyCheckinSchema>;
